import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  ViewChildren,
  QueryList,
  ElementRef,
  AfterViewInit,
  input,
  model,
  computed,
} from '@angular/core';
import { AngularSvgIconModule } from 'angular-svg-icon';

export interface IStep {
  label: string;
  completed: boolean;
}

@Component({
  selector: 'ag1-stepper',
  templateUrl: './ag1-stepper.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AngularSvgIconModule],
})
export class Ag1StepperComponent implements OnInit, AfterViewInit {
  readonly orientation = input<'horizontal' | 'vertical'>('horizontal');

  readonly steps = input<IStep[]>([]);
  readonly activeStep = model<IStep>(undefined);

  // grab step elements in order to scroll to them
  @ViewChildren('stepElement') stepElements: QueryList<ElementRef>;

  constructor() {}

  ngOnInit(): void {
    const steps = this.steps();
    if (!steps?.length) {
      console.error('Steps are required', steps);
    }
  }

  ngAfterViewInit(): void {
    // scroll to the active step after the DOM has been loaded
    const index: number = this.steps().findIndex(
      (step: IStep) => step.label === this.activeStep().label,
    );

    this.scrollToStep(index);
  }

  activateStep(step: IStep): void {
    // only allow activation if the step is completed
    if (!step.completed) {
      return;
    }

    this.activeStep.set(step);

    const index: number = this.steps().findIndex(
      (step: IStep) => step.label === this.activeStep().label,
    );

    // set timeout to ensure that scrolling only happens after the DOM has been updated
    setTimeout(() => {
      this.scrollToStep(index);
    }, 50);
  }

  nextStep(): void {
    this.completeStep(this.activeStep());

    const index: number = this.steps().findIndex(
      (step: IStep) => step.label === this.activeStep().label,
    );
    this.activeStep.set(this.steps()[index + 1]);

    setTimeout(() => {
      this.scrollToStep(index + 1);
    }, 50);
  }

  scrollToStep(index: number): void {
    const elements: ElementRef[] = this.stepElements.toArray();
    elements[index]?.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }

  isActiveStep(step: IStep): boolean {
    return step?.label === this.activeStep()?.label;
  }

  isCompletedStep(step: IStep): boolean {
    return step?.completed;
  }

  completeStep(step: IStep): void {
    step.completed = true;
  }

  readonly isVertical = computed(() => this.orientation() === 'vertical');

  readonly isHorizontal = computed(() => this.orientation() === 'horizontal');
}
